import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';

const SRNPrint = ({ srn, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);

  useEffect(() => {
    if (autoPrint && srn && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [autoPrint, srn]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy hh:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatDateStr = (dateStr) => {
    if (!dateStr) return '-';
    try {
      if (dateStr.includes('-')) {
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}/${y}`;
      }
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    if (!srn) return;

    const items = srn.service_items || [];

    const itemRows = items.map((item, idx) => {
      const amount = (item.quantity || 0) * (item.rate || 0);
      return `
      <tr>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${idx + 1}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">
          <div style="font-weight: 600;">${item.description || ''}</div>
        </td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${item.quantity || 0}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${item.unit || 'NOS'}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">\u20B9${(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">\u20B9${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>
    `;
    }).join('');

    const totalAmount = items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>SRN - ${srn.srn_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 12px; 
            padding: 20px;
            max-width: 210mm;
            margin: 0 auto;
          }
          .header { 
            text-align: center;
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header h1 { 
            font-size: 18px; 
            margin-bottom: 5px;
            color: #333;
          }
          .header h2 {
            font-size: 14px;
            color: #666;
            font-weight: normal;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .info-box {
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fafafa;
          }
          .info-box h3 {
            font-size: 11px;
            color: #666;
            margin-bottom: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #eee;
            text-transform: uppercase;
          }
          .info-box p {
            margin: 4px 0;
            font-size: 11px;
          }
          .info-box .value {
            font-weight: 600;
            color: #333;
          }
          .info-box .label {
            color: #666;
            font-size: 10px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          th { 
            background: #f1f5f9; 
            padding: 8px 6px; 
            border: 1px solid #ddd; 
            text-align: left;
            font-weight: bold;
            color: #334155;
            font-size: 11px;
          }
          td { 
            padding: 6px; 
            border: 1px solid #ddd;
            vertical-align: top;
            font-size: 11px;
          }
          .totals-row {
            background: #f8fafc;
            font-weight: bold;
          }
          .footer {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 20px;
          }
          .signature-box {
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 40px;
            padding-top: 5px;
            font-size: 11px;
          }
          .notes {
            margin-top: 15px;
            padding: 10px;
            background: #fffbeb;
            border: 1px solid #fcd34d;
            border-radius: 4px;
            font-size: 11px;
          }
          .notes h4 {
            margin-bottom: 5px;
            color: #92400e;
          }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SERVICES RECEIVED NOTE (SRN)</h1>
          <h2>${srn.srn_number}</h2>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <h3>SRN Details</h3>
            <p><span class="label">SRN No:</span> <span class="value">${srn.srn_number}</span></p>
            <p><span class="label">Created:</span> <span class="value">${formatDateTime(srn.created_at)}</span></p>
            <p><span class="label">Status:</span> <span class="value">${(srn.status || '').toUpperCase()}</span></p>
          </div>
          <div class="info-box">
            <h3>Supplier Details</h3>
            <p><span class="label">Supplier:</span> <span class="value">${srn.supplier_name}</span></p>
            <p><span class="label">Invoice No:</span> <span class="value">${srn.invoice_no || '-'}</span></p>
            <p><span class="label">Invoice Date:</span> <span class="value">${formatDateStr(srn.invoice_date)}</span></p>
          </div>
          <div class="info-box">
            <h3>Reference</h3>
            <p><span class="label">SPO Number:</span> <span class="value">${srn.spo_number || '-'}</span></p>
            <p><span class="label">Created By:</span> <span class="value">${srn.created_by_name || '-'}</span></p>
            ${srn.is_import_cost ? '<p><span class="label">Type:</span> <span class="value">IMPORT COST</span></p>' : ''}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">S.No</th>
              <th style="min-width: 200px;">Service Description</th>
              <th style="width: 60px; text-align: right;">Qty</th>
              <th style="width: 50px; text-align: center;">Unit</th>
              <th style="width: 80px; text-align: right;">Rate</th>
              <th style="width: 100px; text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
            <tr class="totals-row">
              <td colspan="5" style="text-align: right; padding: 8px;">Sub Total:</td>
              <td style="text-align: right; padding: 8px;">\u20B9${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>

        <div class="info-box" style="margin-bottom: 20px;">
          <h3>Payment Summary</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px;">
            <p><span class="label">Sub Total:</span> <span class="value">\u20B9${(srn.sub_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">GST (${srn.gst_rate || 0}%):</span> <span class="value">\u20B9${(srn.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Other Charges:</span> <span class="value">\u20B9${(srn.other_charges || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Invoice Amount:</span> <span class="value" style="font-weight: bold;">\u20B9${(srn.invoice_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
          </div>
          ${srn.total_advance_deducted > 0 ? `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd; display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px;">
            <p><span class="label">Advance Deducted:</span> <span class="value" style="color: #166534;">\u20B9${(srn.total_advance_deducted || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Balance Payment:</span> <span class="value" style="color: #dc2626; font-weight: bold;">\u20B9${(srn.balance_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Due Date:</span> <span class="value">${srn.payment_due_date ? formatDateStr(srn.payment_due_date) : '-'}</span></p>
          </div>
          ` : `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd; text-align: right;">
            <p style="font-size: 14px;"><span class="label">Total Payable:</span> <span class="value" style="font-size: 16px; font-weight: bold; color: #166534;">\u20B9${(srn.invoice_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
          </div>
          `}
        </div>

        ${srn.remarks ? `
        <div class="notes">
          <h4>Remarks:</h4>
          <p>${srn.remarks}</p>
        </div>
        ` : ''}

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line">Purchase Team</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Accounts</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Authorised Signatory</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        if (onPrintComplete) {
          onPrintComplete();
        }
      }, 250);
    }
  };

  return null;
};

export default SRNPrint;
